import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, Button, ErrorMessage, FormField, TextInput, colors, spacing, typography } from '@bafkal/ui-shared';
import { createApiKey, clearNewlyCreatedKey } from '../apiKeyActions';

export function ApiKeyCreateModal({ isOpen, onClose }) {
  const dispatch = useDispatch();
  const { createLoading, createError, newlyCreatedKey } = useSelector(
    (state) => state.apiKeys
  );

  const [name, setName] = useState('');
  const [copied, setCopied] = useState(false);
  const [validationError, setValidationError] = useState('');

  // Reset local state when modal opens or closes
  useEffect(() => {
    if (!isOpen) {
      setName('');
      setCopied(false);
      setValidationError('');
      if (newlyCreatedKey) {
        dispatch(clearNewlyCreatedKey());
      }
    }
  }, [isOpen, dispatch, newlyCreatedKey]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setValidationError('Please enter a descriptive name for your API key.');
      return;
    }
    setValidationError('');
    try {
      await dispatch(createApiKey(name.trim()));
    } catch (err) {
      // Error captured in Redux
    }
  };

  const handleCopy = async () => {
    if (!newlyCreatedKey?.key) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(newlyCreatedKey.key);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = newlyCreatedKey.key;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch (e) {
      console.error('Failed to copy to clipboard', e);
    }
  };

  const handleClose = () => {
    if (newlyCreatedKey) {
      dispatch(clearNewlyCreatedKey());
    }
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={newlyCreatedKey ? 'API Key Generated' : 'Create New API Key'}
    >
      {newlyCreatedKey ? (
        <div>
          <div
            style={{
              backgroundColor: colors.warning[50],
              borderLeft: `4px solid ${colors.warning[500]}`,
              padding: spacing.md,
              borderRadius: '6px',
              marginBottom: spacing.md,
            }}
          >
            <p
              style={{
                margin: 0,
                color: colors.warning[800],
                fontSize: typography.sizes.sm,
                fontWeight: 500,
              }}
            >
              Make sure to copy your API key now. You won't be able to see it again.
            </p>
          </div>

          <div style={{ marginBottom: spacing.md }}>
            <label
              style={{
                display: 'block',
                fontSize: typography.sizes.sm,
                fontWeight: 600,
                color: colors.neutral[700],
                marginBottom: spacing.xs,
              }}
            >
              Key Name
            </label>
            <p
              style={{
                margin: 0,
                fontSize: typography.sizes.sm,
                color: colors.neutral[900],
                fontWeight: 500,
              }}
            >
              {newlyCreatedKey.name}
            </p>
          </div>

          <div style={{ marginBottom: spacing.lg }}>
            <label
              style={{
                display: 'block',
                fontSize: typography.sizes.sm,
                fontWeight: 600,
                color: colors.neutral[700],
                marginBottom: spacing.xs,
              }}
            >
              Secret API Key
            </label>
            <div style={{ display: 'flex', gap: spacing.sm, alignItems: 'center' }}>
              <input
                readOnly
                value={newlyCreatedKey.key}
                style={{
                  flex: 1,
                  fontFamily: 'monospace',
                  fontSize: typography.sizes.xs,
                  padding: `${spacing.sm} ${spacing.md}`,
                  backgroundColor: colors.neutral[50],
                  border: `1px solid ${colors.neutral[300]}`,
                  borderRadius: '6px',
                  color: colors.neutral[900],
                  height: '42px',
                  boxSizing: 'border-box',
                }}
                data-testid="created-api-key-input"
              />
              <Button
                type="button"
                variant={copied ? 'secondary' : 'primary'}
                onClick={handleCopy}
                data-testid="copy-key-button"
              >
                {copied ? 'Copied!' : 'Copy'}
              </Button>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              type="button"
              variant="primary"
              onClick={handleClose}
              data-testid="close-modal-button"
            >
              Done
            </Button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          {validationError && <ErrorMessage message={validationError} />}
          {createError && (
            <ErrorMessage
              message={
                typeof createError === 'string'
                  ? createError
                  : createError.message || 'Failed to create API key'
              }
            />
          )}

          <FormField
            label="Key Name"
            helperText="Provide a friendly name to identify this key (e.g. Production Web SDK, Mobile App)."
          >
            <TextInput
              id="apiKeyName"
              name="name"
              placeholder="e.g. Production Web SDK"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={createLoading}
              autoFocus
              data-testid="api-key-name-input"
            />
          </FormField>

          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: spacing.sm,
              marginTop: spacing.lg,
            }}
          >
            <Button
              type="button"
              variant="secondary"
              onClick={handleClose}
              disabled={createLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={createLoading || !name.trim()}
              data-testid="submit-create-key-button"
            >
              {createLoading ? 'Generating…' : 'Generate Key'}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}

export default ApiKeyCreateModal;
